import classnames from 'classnames';
import React from 'react';
import styles from './button.css';

export interface SharedButtonProps {
  /** Adds additional classes to the component. */
  className?: string;
  /** Disables button, disallowing interaction */
  disabled?: boolean;
  /** Name added to the input field on the HTML Markup */
  name?: string;
  /** Provides visual elements that identify the primary action */
  primary?: boolean;
  /** Provides visual elements that identify secondary actions */
  secondary?: boolean;
  /** Provides visual elements that identify tertiary actions */
  tertiary?: boolean;
  /** Provides visual elements that indicates dangerous or potentially negative action */
  remove?: boolean;
  /** Provides visual elements for usage on light or dark environments */
  darkEnv?: boolean;
  /** Provides visual elements for large buttons used on forms */
  large?: boolean;
  /** Makes the button full width, usually used on forms */
  fullWidth?: boolean;
}

export interface ButtonProps extends SharedButtonProps {
  /** The content to display inside the button */
  children?: React.ReactNode;
  /** Callback when clicked */
  onClick?: (e: React.MouseEvent | React.FormEvent) => void;
  /** Button type */
  type?: 'submit' | 'reset' | 'button';
  /** Provides visual elements that identify new primary actions */
  quaternary?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  Quaternary = 'quaternary',
  Remove = 'remove'
}

/**
 * Button Component
 *
 * @param {ButtonProps} props
 *
 * @returns JSX.ELEMENT
 */
export const Button = ({
  children,
  className,
  onClick,
  name,
  'data-testid': dataTestId,
  type = 'button',
  disabled = false,
  primary = false,
  secondary = false,
  tertiary = false,
  quaternary = false,
  remove = false,
  darkEnv = false,
  large = false,
  fullWidth = false
}: ButtonProps) => {
  const handleClick = (e: React.MouseEvent) => {
    /* istanbul ignore next */
    e.preventDefault();
    onClick!(e);
  };

  const styleType = (): ButtonType => {
    if (primary) {
      return ButtonType.Primary;
    }
    if (secondary) {
      return ButtonType.Secondary;
    }
    if (tertiary) {
      return ButtonType.Tertiary;
    }
    if (quaternary) {
      return ButtonType.Quaternary;
    }
    if (remove) {
      return ButtonType.Remove;
    }
    return ButtonType.Primary;
  };

  const composedClassName = classnames(
    styles.button,
    {
      [styles[styleType()]]: true,
      [styles.darkEnv]: darkEnv,
      [styles.disabled]: disabled,
      [styles.large]: large,
      [styles.fullWidth]: fullWidth
    },
    className
  );

  return (
    <button
      data-testid={dataTestId}
      className={composedClassName}
      type={type}
      disabled={disabled}
      {...(name ? { name } : {})}
      {...(!disabled && typeof onClick === 'function' ? { onClick: handleClick } : {})}
    >
      {children}
      {disabled ? <div className={styles.chromeHackForDisabledButtonMouseEvents} /> : <noscript />}
    </button>
  );
};

export default Button;
